/**
 * SBDashboard.jsx
 * Society Book Dashboard:
 *   - Stat cards: Total Members, Total Billed, Total Collected, Outstanding
 *   - Donut chart: Collected vs Outstanding
 *   - Click outstanding → member-wise breakdown table
 */
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Users, Receipt, CheckCircle, AlertCircle, X } from 'lucide-react';
import { getMembers, getBills, getOutstanding } from '../../api/societybook.api';
import Button from '../../components/common/Button';

const fmt = n => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Simple SVG donut chart
const DonutChart = ({ collected, outstanding, onClick }) => {
  const total = collected + outstanding;
  if (total === 0) return (
    <div style={{ textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.82rem', padding: '2rem' }}>
      No billing data yet
    </div>
  );

  const r = 54;
  const cx = 70;
  const cy = 70;
  const circumference = 2 * Math.PI * r;
  const collectedPct  = collected / total;
  const outstandingPct = outstanding / total;
  const collectedDash  = collectedPct * circumference;
  const outstandingDash = outstandingPct * circumference;
  const gap = 2;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
      <svg width={140} height={140} viewBox="0 0 140 140">
        {/* Background circle */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--bg-overlay)" strokeWidth={18} />
        {/* Outstanding arc */}
        <circle cx={cx} cy={cy} r={r} fill="none"
          stroke="var(--danger)" strokeWidth={18}
          strokeDasharray={`${outstandingDash - gap} ${circumference - outstandingDash + gap}`}
          strokeDashoffset={circumference * 0.25}
          style={{ cursor: 'pointer' }}
          onClick={onClick}
        />
        {/* Collected arc */}
        <circle cx={cx} cy={cy} r={r} fill="none"
          stroke="var(--success)" strokeWidth={18}
          strokeDasharray={`${collectedDash - gap} ${circumference - collectedDash + gap}`}
          strokeDashoffset={circumference * 0.25 - outstandingDash + gap}
        />
        {/* Center label */}
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize={10}
          fill="var(--text-dim)" fontFamily="Arial">Collection</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fontSize={13}
          fill="var(--text)" fontFamily="Arial" fontWeight="700">
          {total > 0 ? Math.round(collectedPct * 100) : 0}%
        </text>
      </svg>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--success)', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Collected</div>
            <div style={{ fontWeight: 700, color: 'var(--success)', fontSize: '0.9rem' }}>₹{fmt(collected)}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={onClick}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--danger)', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Outstanding</div>
            <div style={{ fontWeight: 700, color: 'var(--danger)', fontSize: '0.9rem' }}>₹{fmt(outstanding)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function SBDashboard({ societyId }) {
  const [loading,      setLoading]      = useState(true);
  const [members,      setMembers]      = useState([]);
  const [bills,        setBills]        = useState([]);
  const [outstanding,  setOutstanding]  = useState([]);
  const [showDrilldown, setShowDrilldown] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, bRes, oRes] = await Promise.all([
        getMembers(societyId),
        getBills(societyId, {}),
        getOutstanding(societyId),
      ]);
      setMembers(mRes.data.data || []);
      setBills(bRes.data.data || []);
      setOutstanding(oRes.data.data || []);
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, [societyId]);

  useEffect(() => { load(); }, [load]);

const billAmt        = b => (b.subTotal || 0) + (b.interestOnDelay || 0) + (b.gstAmount || 0);
const totalBilled    = bills.reduce((s, b) => s + billAmt(b), 0);
const totalCollected = bills.filter(b => b.status === 'paid')
  .reduce((s, b) => s + billAmt(b), 0);
const totalPartial   = bills.filter(b => b.status === 'partially_paid')
  .reduce((s, b) => s + (billAmt(b) - (b.netPayable || 0)), 0);
const collectedAmt   = totalCollected + totalPartial;
const outstandingAmt = bills
  .filter(b => ['generated', 'partially_paid'].includes(b.status))
  .reduce((s, b) => s + (b.netPayable || 0), 0);

  // Members with outstanding dues
  const membersWithDues = outstanding.filter(o => o.balanceType === 'Dr' && o.outstanding > 0)
    .sort((a, b) => b.outstanding - a.outstanding);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total Members', value: members.length,          color: 'var(--gold)',    icon: <Users size={18} /> },
          { label: 'Total Billed',  value: `₹${Number(totalBilled).toLocaleString('en-IN')}`,  color: 'var(--info)',    icon: <Receipt size={18} /> },
          { label: 'Collected',     value: `₹${Number(collectedAmt).toLocaleString('en-IN')}`, color: 'var(--success)', icon: <CheckCircle size={18} /> },
          { label: 'Outstanding',   value: `₹${Number(outstandingAmt).toLocaleString('en-IN')}`, color: 'var(--danger)', icon: <AlertCircle size={18} />, onClick: () => setShowDrilldown(true) },
        ].map(({ label, value, color, icon, onClick }) => (
          <div key={label}
            onClick={onClick}
            style={{
              padding: '1.25rem', background: 'var(--bg-surface)',
              border: `1px solid var(--border-subtle)`, borderRadius: 'var(--radius)',
              cursor: onClick ? 'pointer' : 'default',
              transition: 'all var(--t-fast)',
            }}
            onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor = color; }}
            onMouseLeave={e => { if (onClick) e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-dim)' }}>{label}</span>
              <div style={{ color }}>{icon}</div>
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, color, lineHeight: 1 }}>
              {value}
            </div>
            {onClick && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginTop: '0.35rem' }}>Click to see details →</div>
            )}
          </div>
        ))}
      </div>

      {/* Collection chart */}
      <div className="card" style={{ padding: '1.5rem' }}>
        <h3 style={{ marginBottom: '1.25rem', fontFamily: 'var(--font-display)' }}>Collection Overview</h3>
        <DonutChart
          collected={collectedAmt}
          outstanding={outstandingAmt}
          onClick={() => setShowDrilldown(true)}
        />
      </div>

      {/* Outstanding drill-down panel */}
      {showDrilldown && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex' }}>
          <div style={{ flex: 1, background: 'rgba(0,0,0,0.5)' }} onClick={() => setShowDrilldown(false)} />
          <div style={{ width: 480, background: 'var(--bg-surface)', borderLeft: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.2s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border-subtle)' }}>
              <div>
                <h3>Outstanding Dues</h3>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                  {membersWithDues.length} member{membersWithDues.length !== 1 ? 's' : ''} with pending dues
                </p>
              </div>
              <button onClick={() => setShowDrilldown(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem' }}>
              {membersWithDues.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  <CheckCircle size={36} strokeWidth={1} style={{ margin: '0 auto 0.75rem', display: 'block', opacity: 0.4 }} />
                  <div style={{ fontWeight: 600 }}>All clear!</div>
                  <div style={{ fontSize: '0.82rem', marginTop: '0.3rem' }}>No outstanding dues</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {membersWithDues.map((o, i) => (
                    <div key={o.memberId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.85rem 1rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--danger-bg)', border: '1px solid rgba(240,107,107,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: 'var(--danger)', flexShrink: 0 }}>
                          {i + 1}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text)' }}>{o.memberName}</div>
                          <span className="flat-tag" style={{ fontSize: '0.72rem' }}>{o.flatNo}</span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 700, color: 'var(--danger)', fontSize: '0.9rem' }}>₹{fmt(o.outstanding)}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>Dr</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Total at bottom */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontWeight: 600 }}>Total Outstanding</span>
              <span style={{ fontWeight: 700, color: 'var(--danger)', fontSize: '1rem' }}>
                ₹{fmt(membersWithDues.reduce((s, o) => s + o.outstanding, 0))}
              </span>
            </div>
          </div>
          <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
        </div>
      )}
    </div>
  );
}
